import {
  GraphCard,
  zonedToUTC,
  zonedToUTCAdd1Day,
  useTranslation
} from '@fareye/common'
import { Flex, Graph, RenderControl, InfoIcon, Span } from '@fareye/ui'
import {
  AxisFormatter,
  cancelToken,
  isEmpty,
  responseParser,
  post,
  groupBy
} from '@fareye/utils'
import React from 'react'
import { inTransitConstant } from '../../../config/constants'
import queryString from 'query-string'
import { getYKeys } from '../../../utility/funcs'
import { IRouteDetails } from '../../../utility/types'
import { PieGraph } from '../components/Pie'

import MultiModalTransporterCard from '../components/MultiModalTransporterCard'

const MultiModalRouteDetails = ({
  match,
  config,
  endDate,
  location,
  startDate,
  globalSearch
}: IRouteDetails) => {
  const { route }: any = match.params
  const { transportMode = 'road', stops = 0 } = queryString.parse(
    location.search
  )
  const source = cancelToken().source()

  const {
    config: { type = 'Journey', apiConfig, metricsCount, graphsToShow = {} },
    styling: { style, className, variant }
  } = config
  const {
    tripStatusGraph,
    exceptionsGraph,
    transporterPerformanceGraph
  } = graphsToShow
  const [apiResponse, setApiResponse] = React.useState<any>({
    data: [],
    isLoading: false,
    isError: false,
    isSuccess: false,
    message: ''
  })
  const [exceptionsData, setExceptionsData] = React.useState<any>([])

  const getRoute = (route: string) => {
    route = decodeURIComponent(route)
    const [origin, destination] = route.split('-')
    return origin !== 'null'
      ? destination !== 'null'
        ? route
        : origin
      : destination
  }

  function generateFilterDataFromGlobalSearch(globalSearch: any[]) {
    if (isEmpty(globalSearch)) {
      return []
    } else {
      const groupedSearch = groupBy(globalSearch, ele => ele.key)
      return Object.entries(groupedSearch).map(([key, value]) => {
        return [
          {
            filterKey: key,
            filterValue: value.reduce((res = [], obj) => {
              res.push(obj.value)
              return res
            }, []),
            condition: 'include',
            filterType: 'eq'
          }
        ]
      })
    }
  }

  function generateFilterObjectForDateRange(startDate: any, endDate: any) {
    const startFilter = [
      {
        filterKey: 'start_date',
        filterValue: startDate ? zonedToUTC(startDate) : new Date(),
        condition: 'include',
        filterType: 'gte'
      }
    ]
    const endFilter = [
      {
        filterKey: 'start_date',
        filterValue: endDate ? zonedToUTCAdd1Day(endDate) : new Date(),
        condition: 'include',
        filterType: 'lt'
      }
    ]
    const locationFilter = [
      {
        filterKey: 'route_string',
        filterValue: getRoute(route),
        condition: 'include',
        filterType: 'eq'
      }
    ]
    const stopsCount = [
      {
        filterKey: 'stops_count',
        filterValue: stops || 0,
        condition: 'include',
        filterType: 'eq'
      }
    ]
    const transportModeParam = [
      {
        filterKey:
          type === 'Journey' ? 'transport_mode' : 'package_legs.transport_mode',
        filterValue: transportMode,
        condition: 'include',
        filterType: 'eq'
      }
    ]
    const isActiveFilter = [
      {
        filterKey: 'is_active',
        filterValue: false,
        condition: 'include',
        filterType: 'eq'
      }
    ]
    return [
      startFilter,
      endFilter,
      locationFilter,
      stopsCount,
      transportModeParam,
      isActiveFilter
    ]
  }

  async function fetchData() {
    try {
      setApiResponse({ ...apiResponse, isLoading: true })
      let filter: any = generateFilterDataFromGlobalSearch(globalSearch)
      const dateFilter = generateFilterObjectForDateRange(startDate, endDate)
      filter = [...filter, ...dateFilter]
      const {
        data: { vehicleMetrics, transporterPerformanceCard, alertsTriggered },
        message,
        error
      } = responseParser(
        await post(
          `${apiConfig && apiConfig.apiURL}`,
          {
            filter,
            perPage: 10000,
            page: 0,
            aggs: ['status']
            // ...generateSortingObj()
          },
          source.token
        )
      )
      if (error) {
        setApiResponse({
          ...apiResponse,
          isError: true,
          data: [],
          isSuccess: true,
          isLoading: false,
          message
        })
      } else {
        setApiResponse({
          ...apiResponse,
          isError: false,
          data: { vehicleMetrics, alertsTriggered, transporterPerformanceCard },
          isSuccess: true,
          isLoading: false,
          message:
            apiConfig &&
            apiConfig.apiDataState &&
            apiConfig.apiDataState.noDataMessage
        })
      }
    } catch (err:any) {
      const { message, error } = responseParser(err.response)
      setApiResponse({
        ...apiResponse,
        isSuccess: false,
        isError: true,
        isLoading: false,
        message
      })
      console.error('App Error:', error)
    }
  }
  React.useEffect(() => {
    fetchData()
    return () => {
      source.cancel('Unsubscribe Before Unmount')
    }
    // eslint-disable-line
  }, [globalSearch, startDate, endDate])

  const {
    data: {
      vehicleMetrics = {},
      alertsTriggered = {},
      transporterPerformanceCard = {}
    }
  } = apiResponse
  const metrics: any = {}
  vehicleMetrics &&
    Object.keys(vehicleMetrics).forEach((item: string) => {
      metrics[item] = vehicleMetrics[item].count
    })

  React.useEffect(() => {
    const exceptions: Array<any> = []
    Object.keys(alertsTriggered).forEach((item: string) => {
      exceptions.push({ label: AxisFormatter(item), ...alertsTriggered[item] })
    })
    setExceptionsData(exceptions.slice(0, 20))
  }, [apiResponse.data])

  const { t } = useTranslation()

  const tripStatusHeading = (
    <>
      {t('Vehicle Metrics')}
      <InfoIcon
        style={{ marginLeft: 5 }}
        title={t('Vehicle Completion Status')}
      />
    </>
  )

  const exceptionHeading = (
    <>
      {t(inTransitConstant.alertsLabel)}
      <InfoIcon
        style={{ marginLeft: 5 }}
        title={t('Alerts Wise Distribution')}
      />
    </>
  )

  return (
    <RenderControl state={apiResponse} variant={'m'}>
      <Flex
        p={3}
        style={{
          width: 'calc(100% - 32px)',
          overflow: 'auto hidden',
          ...style
        }}
        className={className}
      >
        <Flex style={{ padding: '2px 4px', overflow: 'auto hidden' }}>
          {tripStatusGraph?.show && !isEmpty(metrics) && (
            <GraphCard
              heading={tripStatusHeading}
              data-config-id="tripStatusGraph"
            >
              <PieGraph
                data={metrics}
                metric={metricsCount}
                sumOfTrips={Object.values(metrics).reduce(
                  (acc: number, num: any) => acc + num,
                  0
                )}
              />
            </GraphCard>
          )}
          <GraphCard graphId="exceptions" heading={exceptionHeading}>
            {exceptionsGraph?.show && !isEmpty(exceptionsData) ? (
              <div>
                <Graph
                  data={exceptionsData}
                  type="bar"
                  config={{
                    colors: {},
                    legendsList: ['']
                  }}
                  ratio={1.3}
                  selector="exceptions"
                  XLabel={t('Date')}
                  YLabel={t('Count of Alerts')}
                  XLabelKey={'label'}
                  YLabelKey={getYKeys(exceptionsData)}
                  stacked="tower"
                />
              </div>
            ) : (
              <Span>{t(inTransitConstant.noAlertsLabel)}</Span>
            )}
          </GraphCard>

          <MultiModalTransporterCard
            transporterData={transporterPerformanceCard}
          />
        </Flex>
      </Flex>
    </RenderControl>
  )
}
export default MultiModalRouteDetails
